import { type ReactNode, useId } from 'react';
import clsx from 'clsx';
import { Heading, Paragraph } from '../typography';
import styles from './StateMessage.module.css';

export interface Props {
  title: string;
  description: string;
  layout?: 'page' | 'section';
  /**
   * `well` sets a section message in a full-width `gray-50` box, for a message that stands in for
   * a view's whole content, such as an empty tab. Section layout only.
   */
  surface?: 'plain' | 'well';
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  headingId?: string;
  /** Use Button or LinkButton; the caller owns navigation and retry state. */
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  /** Announce a newly resolved outcome, not a static message on initial page load. */
  announce?: boolean;
}

/** Presentation of a resolved state. Loading and permission decisions belong to the caller. */
export function StateMessage({
  title,
  description,
  layout = 'page',
  surface = 'plain',
  headingLevel,
  headingId,
  primaryAction,
  secondaryAction,
  announce = false,
}: Props) {
  const generatedId = useId();
  const titleId = headingId ?? generatedId;
  const isPage = layout === 'page';
  return (
    <section
      className={clsx(
        styles.message,
        isPage ? styles.page : styles.section,
        !isPage && surface === 'well' && styles.well,
      )}
      aria-labelledby={titleId}
    >
      <div className={styles.copy}>
        {/* A section message is the `lg` row of the title table in design/10-foundations.md: a
            16px title over 14px/20px copy. At 20px over 16px it outranked the section around it. */}
        <Heading
          id={titleId}
          level={headingLevel ?? (isPage ? 1 : 2)}
          family="primary"
          size={isPage ? '2xl' : 'lg'}
          height={isPage ? '2xl' : 'lg'}
          weight="semibold"
          // Set rather than inherited: a section message sits inside hosts that colour their own
          // text, such as a table's gray-600 empty row, and the title must stay gray-900 in all.
          color="gray-900"
          textWrap={isPage ? 'balance' : undefined}
        >
          {title}
        </Heading>
        <div role={announce ? 'status' : undefined} aria-atomic={announce ? true : undefined}>
          <Paragraph
            family="primary"
            size={isPage ? 'lg' : 'md'}
            height={isPage ? 'lg' : 'sm'}
            color="gray-600"
            textWrap={isPage ? 'balance' : undefined}
          >
            {description}
          </Paragraph>
        </div>
      </div>
      {(primaryAction || secondaryAction) && (
        <div className={styles.actions}>
          {secondaryAction}
          {primaryAction}
        </div>
      )}
    </section>
  );
}
